import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  OnChangeFn,
  SortingState,
  useReactTable
} from "@tanstack/react-table";

import { AllConnectedObjectType, COwithoutFeatDataType } from "../../types/connected-object";
import { displayDate } from "../../utils/date";
import { getEquipmentSiteName } from "../../utils/equipment";
import useEquipment from "../../hooks/useEquipment";
import Paginate from "../ui/pagination/Paginate";
import EditIcon from "../icons/Edit";
import LockIcon from "../icons/Lock";
import DeleteIcon from "../icons/Delete";

const COLUMN_TO_ORDERBY: Record<string, string> = {
  url: "url",
  site: "site",
  designation: "designation",
  location: "location",
  status: "isBlocked",
  updatedAt: "updatedAt"
};

const ORDERBY_TO_COLUMN: Record<string, string> = {
  url: "url",
  site: "site",
  designation: "designation",
  location: "location",
  isBlocked: "status",
  updatedAt: "updatedAt",
  id: "url",
  createdAt: "updatedAt",
  activatedAt: "updatedAt"
};

type ManageAllConnectedObjectProps = {
  allObj: Omit<AllConnectedObjectType, "data">;
  data: COwithoutFeatDataType[] | null;
  onFetchPage: (page: number) => void;
  orderby: string;
  order: "ASC" | "DESC";
  onSortChange: (orderby: string, order: "ASC" | "DESC") => void;
};

const ManageAllConnectedObject = ({
  allObj,
  data,
  onFetchPage,
  orderby,
  order,
  onSortChange
}: ManageAllConnectedObjectProps) => {
  const currentPageRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const intl = useIntl();
  const { removeEquipment, response, isLoading: isDeleting } = useEquipment();
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const sorting = useMemo<SortingState>(() => {
    const columnId = ORDERBY_TO_COLUMN[orderby] ?? "url";
    return [{ id: columnId, desc: order === "DESC" }];
  }, [orderby, order]);

  const handleSortingChange = useCallback<OnChangeFn<SortingState>>((updater) => {
    const nextSorting = typeof updater === "function" ? updater(sorting) : updater;
    const next = nextSorting[0];
    if (!next) {
      onSortChange("url", "ASC");
      return;
    }
    const apiOrderBy = COLUMN_TO_ORDERBY[next.id];
    if (!apiOrderBy) return;
    onSortChange(apiOrderBy, next.desc ? "DESC" : "ASC");
  }, [sorting, onSortChange]);

  useEffect(() => {
    if (allObj && currentPageRef.current) {
      currentPageRef.current.value = allObj.page.toString();
    }
  }, [allObj]);

  useEffect(() => {
    if (response && pendingDeleteId !== null) {
      setPendingDeleteId(null);
      onFetchPage(allObj.page);
    }
  }, [response, pendingDeleteId, onFetchPage, allObj.page]);

  const handleEdit = useCallback(
    (obj: COwithoutFeatDataType) => {
      const listSearch = searchParams.toString();
      navigate(`add?id=${obj.url}`, {
        state: { listSearch: listSearch ? `?${listSearch}` : "" }
      });
    },
    [navigate, searchParams]
  );

  const handleDelete = useCallback(
    (obj: COwithoutFeatDataType) => {
      const equipmentId = obj.equipment?.id;
      if (!equipmentId) return;

      const confirmed = window.confirm(
        intl.formatMessage(
          { id: "equipment.list.confirmDelete" },
          { name: obj.equipment?.designation || obj.url }
        )
      );
      if (!confirmed) return;

      setPendingDeleteId(equipmentId);
      removeEquipment(equipmentId);
    },
    [removeEquipment, intl]
  );

  const columns = useMemo<ColumnDef<COwithoutFeatDataType>[]>(
    () => [
      {
        id: "url",
        accessorKey: "url",
        header: "#ID",
        meta: { width: "12%" },
        cell: (info) => (
          <span className="font-medium text-gray-900">{info.getValue<string>()}</span>
        )
      },
      {
        id: "site",
        accessorFn: (row) =>
          getEquipmentSiteName(row.equipment) || intl.formatMessage({ id: "common.dash" }),
        header: intl.formatMessage({ id: "equipment.list.colSite" }),
        meta: { width: "15%" },
        cell: (info) => info.getValue<string>()
      },
      {
        id: "designation",
        accessorFn: (row) =>
          row.equipment?.designation ||
          row.name ||
          intl.formatMessage({ id: "common.notConfigured" }),
        header: intl.formatMessage({ id: "equipment.list.colEquipment" }),
        meta: { width: "25%" },
        cell: (info) => info.getValue<string>()
      },
      {
        id: "location",
        accessorFn: (row) =>
          row.equipment?.location || intl.formatMessage({ id: "common.dash" }),
        header: intl.formatMessage({ id: "equipment.list.colLocation" }),
        meta: { width: "23%" },
        cell: (info) => info.getValue<string>()
      },
      {
        id: "status",
        accessorFn: (row) => row.isBlocked,
        header: intl.formatMessage({ id: "equipment.list.colStatus" }),
        meta: { width: "10%" },
        cell: (info) => {
          const isBlocked = info.getValue<boolean>();
          return (
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                !isBlocked
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {intl.formatMessage({ id: isBlocked ? "common.locked" : "common.active" })}
            </span>
          );
        }
      },
      {
        id: "updatedAt",
        accessorFn: (row) => row.updatedAt || row.activatedAt || "",
        header: intl.formatMessage({ id: "equipment.list.colUpdatedAt" }),
        meta: { width: "10%" },
        cell: (info) => {
          const row = info.row.original;
          return (
            <span className="text-gray-500">
              {displayDate(row.updatedAt || row.activatedAt)}
            </span>
          );
        }
      },
      {
        id: "actions",
        enableSorting: false,
        header: "",
        meta: { width: "5%" },
        cell: ({ row }) => {
          const obj = row.original;
          return (
            <div className="flex items-center gap-2">
              <button
                className="text-gray-600 hover:text-orange-500"
                onClick={() => handleEdit(obj)}
                title={intl.formatMessage({ id: "common.edit" })}
                type="button"
              >
                <EditIcon className="w-5 h-5" />
              </button>
              <button
                className="text-gray-600 hover:text-orange-500"
                title={intl.formatMessage({ id: "equipment.list.lock" })}
                type="button"
              >
                <LockIcon className="w-5 h-5" />
              </button>
              <button
                disabled={!obj.equipment || isDeleting}
                className="text-red-500 hover:text-red-700 disabled:text-gray-300"
                onClick={() => handleDelete(obj)}
                title={intl.formatMessage({ id: "common.delete" })}
                type="button"
              >
                <DeleteIcon className="w-5 h-5" />
              </button>
            </div>
          );
        }
      }
    ],
    [handleEdit, handleDelete, isDeleting, intl]
  );

  const table = useReactTable({
    data: data ?? [],
    columns,
    state: { sorting },
    onSortingChange: handleSortingChange,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.id)
  });

  if (!data || data.length === 0) {
    return (
      <div className="py-12 flex flex-col items-center justify-center">
        <p className="text-lg text-gray-500">
          {intl.formatMessage({ id: "equipment.list.emptyTable" })}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-y-3">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  const width = (header.column.columnDef.meta as { width?: string } | undefined)
                    ?.width;

                  return (
                    <th
                      key={header.id}
                      className="px-4 py-2 text-left text-sm font-normal text-gray-600"
                      style={{ width }}
                    >
                      {header.isPlaceholder ? null : (
                        <button
                          type="button"
                          className={`inline-flex items-center gap-1 ${
                            canSort ? "cursor-pointer select-none hover:text-gray-900" : ""
                          }`}
                          onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                          disabled={!canSort}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort && (
                            <span className="text-xs text-gray-400" aria-hidden>
                              {sorted === "asc" ? "▲" : sorted === "desc" ? "▼" : "↕"}
                            </span>
                          )}
                        </button>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell, cellIndex) => {
                  const isFirst = cellIndex === 0;
                  const isLast = cellIndex === row.getVisibleCells().length - 1;
                  const baseStyle: CSSProperties = isFirst
                    ? { border: "1px solid #E4E7EC", borderRight: "none" }
                    : isLast
                      ? { border: "1px solid #E4E7EC", borderLeft: "none" }
                      : { borderTop: "1px solid #E4E7EC", borderBottom: "1px solid #E4E7EC" };

                  return (
                    <td
                      key={cell.id}
                      className={`px-4 py-4 text-left text-sm text-gray-700 ${
                        isFirst ? "rounded-l-2xl" : ""
                      } ${isLast ? "rounded-r-2xl" : ""}`}
                      style={baseStyle}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {allObj.totalPages > 1 && (
        <div className="mt-6 flex justify-end">
          <Paginate {...allObj} to={(page) => onFetchPage(page)} />
        </div>
      )}
    </>
  );
};

export default ManageAllConnectedObject;
